<section class="veeva-taxonomy-section" aria-labelledby="veeva-taxonomy-heading">
  <h4 id="veeva-taxonomy-heading" class="section-title">Taxonomy validation</h4>
  <p class="section-description">
    Probe your connected Vault to see which taxonomy fields exist and choose which appear as main
    filters (outside Advanced) during import and sync.
  </p>

  @if (!veevaConfigured()) {
    <div class="notice-banner" role="status">
      <mat-icon aria-hidden="true">info</mat-icon>
      <div>
        <strong>Veeva is not connected yet.</strong>
        <span>Save your Veeva credentials above to enable taxonomy validation.</span>
      </div>
    </div>
  } @else if (!isPersisted()) {
    <div class="notice-banner" role="status">
      <mat-icon aria-hidden="true">info</mat-icon>
      <div>
        <strong>Taxonomies not validated yet.</strong>
        @if (isAdmin()) {
          <span>Run validation to persist the taxonomy set for this Vault.</span>
        } @else {
          <span>Ask an admin to validate taxonomies for this Vault.</span>
        }
      </div>
    </div>
  }

  @if (validatedAt(); as validated) {
    <p class="validated-at">Last validated: {{ validated | date: 'medium' }}</p>
  }

  @if (errorMessage(); as error) {
    <div class="error-banner" role="alert">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <div>
        <span>{{ error }}</span>
        @if (showSessionReset() && isAdmin()) {
          <button
            type="button"
            mat-stroked-button
            class="reset-session-btn"
            [disabled]="resettingSession() || validating()"
            (click)="resetVeevaSession()"
          >
            @if (resettingSession()) {
              Resetting…
            } @else {
              Reset Veeva session
            }
          </button>
        }
      </div>
    </div>
  }

  @if (!veevaConfigured()) {
    <p class="empty-state">Connect Veeva first, then validate taxonomies here.</p>
  } @else if (loading()) {
    <div class="loading-row" aria-live="polite">
      <mat-spinner diameter="24" aria-label="Loading taxonomies" />
      <span>Loading taxonomies…</span>
    </div>
  } @else if (fields().length > 0) {
    <div class="taxonomy-table-wrap">
      <table class="taxonomy-table">
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Type</th>
            <th scope="col">Exists</th>
            <th scope="col">Queryable</th>
            @if (isAdmin()) {
              <th scope="col">Main</th>
            }
          </tr>
        </thead>
        <tbody>
          @for (field of fields(); track field.name) {
            <tr [class.unavailable]="!field.exists">
              <td>
                <span class="field-label">{{ field.label }}</span>
                <span class="field-name">{{ field.name }}</span>
              </td>
              <td>{{ field.type ?? '—' }}</td>
              <td>
                <span class="badge" [class.badge-success]="field.exists" [class.badge-muted]="!field.exists">
                  {{ field.exists ? 'Yes' : 'No' }}
                </span>
              </td>
              <td>
                <span
                  class="badge"
                  [class.badge-success]="field.queryable"
                  [class.badge-muted]="!field.queryable"
                >
                  {{ field.queryable ? 'Yes' : 'No' }}
                </span>
              </td>
              @if (isAdmin()) {
                <td>
                  <mat-slide-toggle
                    [checked]="isMainField(field)"
                    [disabled]="!field.exists || saving() || validating()"
                    [attr.aria-label]="'Mark ' + field.label + ' as main taxonomy'"
                    (change)="toggleMainField(field, $event)"
                  />
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
  } @else {
    <p class="empty-state">No taxonomy fields returned from the Vault.</p>
  }

  @if (isAdmin() && veevaConfigured()) {
    <div class="actions">
      @if (!isPersisted()) {
        <button
          type="button"
          mat-raised-button
          color="accent"
          [disabled]="validating() || loading()"
          (click)="validateTaxonomies()"
        >
          @if (validating()) {
            Validating…
          } @else {
            Validate now
          }
        </button>
      } @else {
        <button
          type="button"
          mat-stroked-button
          [disabled]="validating() || loading() || saving()"
          (click)="validateTaxonomies()"
        >
          @if (validating()) {
            Re-checking…
          } @else {
            Re-check taxonomies
          }
        </button>
      }

      <button
        type="button"
        mat-raised-button
        color="accent"
        [disabled]="!hasMainChanges() || saving() || validating() || loading()"
        (click)="saveMainTaxonomies()"
      >
        @if (saving()) {
          Saving…
        } @else {
          Save main selection
        }
      </button>
    </div>
  }
</section>
